import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  FileText, Upload, Trash2, Loader2, AlertCircle, X, Eye, Plus,
  CheckCircle2, Clock, RefreshCw, Pencil, Download, MessageSquare,
  ChevronDown, AlertTriangle, FileUp, Send, FilterX, Check,
} from 'lucide-react';
import ExecutionPanel from '../vdr/ExecutionPanel';
import { AdminMarkPhysicalPanel } from '../vdr/SignaturePanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_BYTES = 20 * 1024 * 1024;

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'shared', label: 'Shared' },
  { value: 'final', label: 'Final' },
];

const ACTION_LABEL_MAP = {
  document_uploaded: 'Document uploaded',
  document_shared: 'Document shared',
  new_version_uploaded: 'New version uploaded',
  approvals_reset: 'Approvals reset',
  document_viewed: 'Document viewed',
  approval_given: 'Approved',
  changes_requested: 'Changes requested',
  contact_added_to_document: 'Party added to document',
  contact_removed_from_document: 'Party removed from document',
  document_deleted: 'Document deleted',
  reminder_sent: 'Reminder sent',
  user_logged_in: 'User signed in',
  user_logged_out: 'User signed out',
};
const prettyAction = (a) => ACTION_LABEL_MAP[a] || a || '—';

const fmtDateTime = (s) => {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};
const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return '—'; }
};
const fmtBytes = (b) => {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};

const StatusPill = ({ status }) => {
  const cfg = {
    draft: 'bg-gray-600/30 text-gray-300',
    shared: 'bg-blue-500/20 text-blue-300',
    final: 'bg-[#00E87A]/20 text-[#00E87A]',
  }[status] || 'bg-gray-600/30 text-gray-300';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg}`}>{status || '—'}</span>;
};

const ApprovalSummaryCell = ({ summary }) => {
  if (!summary || summary.total === 0)
    return <span className="text-xs text-gray-500">No reviewers</span>;
  const { approved, pending, changes_requested, total } = summary;
  let dotClr = 'bg-orange-400';
  if (changes_requested > 0) dotClr = 'bg-red-400';
  else if (approved === total) dotClr = 'bg-[#00E87A]';
  return (
    <span className="inline-flex items-center gap-2 text-sm">
      <span className={`w-2 h-2 rounded-full ${dotClr}`} />
      <span className="text-gray-200">{approved} of {total} approved</span>
      {changes_requested > 0 && <span className="text-red-400 text-xs">· {changes_requested} changes</span>}
    </span>
  );
};

// ─── File picker (shared) ─────────────────────────────────────────────────
const FilePicker = ({ file, onChange, error, setError }) => {
  const inputRef = useRef(null);
  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) handle(f);
  };
  const handle = (f) => {
    setError('');
    if (!f.name.toLowerCase().endsWith('.pdf') && f.type !== 'application/pdf') {
      setError('Only PDF files are allowed'); return;
    }
    if (f.size > MAX_BYTES) { setError('File exceeds 20MB limit'); return; }
    onChange(f);
  };
  return (
    <div>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-gray-700 hover:border-[#00E87A]/50 rounded-lg p-6 text-center cursor-pointer transition-colors bg-[#0D1117]"
        data-testid="file-drop-zone"
      >
        <FileUp className="w-8 h-8 text-gray-500 mx-auto mb-2" />
        {file ? (
          <>
            <p className="text-sm text-white">{file.name}</p>
            <p className="text-xs text-gray-500 mt-0.5">{fmtBytes(file.size)} · click to replace</p>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-300">Drop a PDF here or click to upload</p>
            <p className="text-xs text-gray-500 mt-0.5">PDF only · max 20MB</p>
          </>
        )}
        <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="hidden"
               onChange={(e) => e.target.files?.[0] && handle(e.target.files[0])}
               data-testid="file-input" />
      </div>
      {error && (
        <div className="mt-2 text-sm text-red-400 flex items-center gap-1.5">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}
    </div>
  );
};

// ─── Party Picker (shared by Upload + Edit) ───────────────────────────────
const ROLE_OPTIONS = ['Investor', 'Advisor', 'Co-Founder', 'Legal Counsel', 'Director', 'Other'];

const initials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const PartyPicker = ({ initialParties, selected, onChange, companyId, token, showToast, onPartyCreated }) => {
  const [added, setAdded] = useState([]);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Investor');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(false);
  const dropRef = useRef(null);

  const parties = useMemo(() => {
    const ids = new Set((initialParties || []).map((p) => p.id));
    return [...(initialParties || []), ...added.filter((p) => !ids.has(p.id))];
  }, [initialParties, added]);

  const enabledParties = parties.filter((p) => p.is_vdr_enabled);
  const byId = useMemo(() => Object.fromEntries(enabledParties.map((p) => [p.id, p])), [enabledParties]);
  const selectedParties = selected.map((id) => byId[id]).filter(Boolean);

  const toggleParty = (id) => {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    onChange(next);
  };
  const removeParty = (id) => onChange(selected.filter((x) => x !== id));
  const resetForm = () => { setName(''); setEmail(''); setRole('Investor'); setErr(''); };

  // close dropdown on outside click
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const submitNew = async () => {
    setErr('');
    if (!name.trim()) { setErr('Name is required'); return; }
    if (!email.trim()) { setErr('Email is required'); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/companies/${companyId}/contacts`,
        { name: name.trim(), email: email.trim().toLowerCase(), role_label: role, is_vdr_enabled: true },
        { headers: { Authorization: `Bearer ${token}` } });
      const newParty = r.data;
      setAdded((prev) => [...prev, newParty]);
      onChange([...selected, newParty.id]);
      onPartyCreated?.(newParty);
      showToast?.(`${newParty.name} added and will receive an invite`);
      resetForm();
      setAdding(false);
      // Re-open the dropdown so the user sees the new party is checked and
      // visible in the list alongside any pre-existing parties.
      setOpen(true);
    } catch (e) {
      if (e.response?.status === 409) setErr('This email is already a party on this company');
      else setErr(e.response?.data?.detail || 'Failed to add party');
    } finally { setSubmitting(false); }
  };

  const noneEnabled = enabledParties.length === 0;
  const placeholder = selectedParties.length > 0
    ? `${selectedParties.length} ${selectedParties.length === 1 ? 'party' : 'parties'} selected`
    : 'Select parties to share with…';

  return (
    <div>
      <label className="block text-sm text-gray-300 mb-1.5">Share with Parties</label>

      {noneEnabled ? (
        <div className="bg-[#0D1117] border border-gray-700 rounded-lg p-4 text-center" data-testid="picker-empty">
          <p className="text-xs text-gray-400 mb-1">No parties have Data Room access yet.</p>
          <p className="text-[11px] text-gray-500 mb-2">Add parties from the Parties tab first.</p>
          {!adding && (
            <button type="button" onClick={() => setAdding(true)} className="text-xs text-[#00E87A] hover:underline font-medium" data-testid="picker-add-new-empty">
              + Add New Party
            </button>
          )}
        </div>
      ) : (
        <div className="relative" ref={dropRef}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="w-full bg-[#0D1117] border border-gray-700 hover:border-gray-600 rounded-lg px-3 py-2.5 flex items-center justify-between text-left focus:border-[#00E87A] focus:outline-none transition-colors"
            data-testid="picker-trigger"
            aria-expanded={open}
          >
            <span className={`text-sm ${selectedParties.length === 0 ? 'text-gray-500' : 'text-white'}`}>
              {placeholder}
            </span>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>

          {open && (
            <div
              className="absolute z-30 mt-1.5 w-full bg-[#161B22] border border-gray-700 rounded-lg shadow-xl overflow-hidden"
              data-testid="picker-dropdown"
            >
              <div className="max-h-56 overflow-y-auto p-1">
                {enabledParties.map((p) => {
                  const checked = selected.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggleParty(p.id)}
                      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left transition-colors ${
                        checked ? 'bg-[#00E87A]/10 hover:bg-[#00E87A]/15' : 'hover:bg-white/5'
                      }`}
                      data-testid={`picker-party-${p.id}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${
                        checked ? 'bg-[#00E87A] text-black' : 'bg-gray-700 text-gray-200'
                      }`}>
                        {initials(p.name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white truncate">
                          {p.name}
                          <span className="text-gray-500 font-normal"> — {p.role_label}</span>
                        </p>
                        <p className="text-[11px] text-gray-500 truncate">{p.email}</p>
                      </div>
                      {checked && <Check className="w-4 h-4 text-[#00E87A] shrink-0" />}
                    </button>
                  );
                })}
              </div>
              {!adding && (
                <button
                  type="button"
                  onClick={() => { setAdding(true); setOpen(false); }}
                  className="w-full text-left px-3 py-2 text-xs text-[#00E87A] hover:bg-white/5 border-t border-gray-700/60 font-medium"
                  data-testid="picker-add-new-btn"
                >
                  + Add New Party
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Selected pills */}
      {selectedParties.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="picker-pills">
          {selectedParties.map((p) => (
            <span
              key={p.id}
              className="inline-flex items-center gap-1.5 bg-[#00E87A]/15 border border-[#00E87A]/30 text-white text-xs rounded-full pl-2 pr-1 py-0.5"
              data-testid={`picker-pill-${p.id}`}
            >
              {p.name}
              <button
                type="button"
                onClick={() => removeParty(p.id)}
                className="w-4 h-4 rounded-full hover:bg-white/10 inline-flex items-center justify-center text-gray-400 hover:text-white"
                aria-label={`Remove ${p.name}`}
                data-testid={`picker-pill-remove-${p.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Inline add new (used by both empty + populated states) */}
      {adding && (
        <div className="mt-2 bg-[#161B22] border border-[#00E87A]/30 rounded-lg p-3 space-y-2" data-testid="picker-inline-form">
          <p className="text-xs text-gray-400 mb-1">Quick add — they&apos;ll be invited immediately.</p>
          <div className="grid grid-cols-2 gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name"
                   className="bg-[#0D1117] border border-gray-700 rounded px-2 py-1.5 text-sm text-white focus:border-[#00E87A] focus:outline-none"
                   data-testid="picker-name-input" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" type="email"
                   className="bg-[#0D1117] border border-gray-700 rounded px-2 py-1.5 text-sm text-white focus:border-[#00E87A] focus:outline-none"
                   data-testid="picker-email-input" />
          </div>
          <select value={role} onChange={(e) => setRole(e.target.value)}
                  className="w-full bg-[#0D1117] border border-gray-700 rounded px-2 py-1.5 text-sm text-white focus:border-[#00E87A] focus:outline-none"
                  data-testid="picker-role-select">
            {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          {err && (
            <div className="text-xs text-red-400 flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {err}
            </div>
          )}
          <div className="flex items-center justify-end gap-3 pt-1">
            <button type="button" onClick={() => { setAdding(false); resetForm(); }}
                    className="text-xs text-gray-400 hover:text-white" data-testid="picker-cancel-btn">Cancel</button>
            <button type="button" onClick={submitNew} disabled={submitting}
                    className="px-3 py-1.5 bg-[#00E87A] hover:bg-[#00d96e] text-black rounded text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="picker-add-share-btn">
              {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
              Add &amp; Share
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


// ─── Upload Document modal ────────────────────────────────────────────────
const UploadDocModal = ({ companyId, token, parties, onClose, onUploaded, showToast, onPartyCreated }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [reviewerInstructions, setReviewerInstructions] = useState('');
  const [status, setStatus] = useState('draft');
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState([]);
  const [file, setFile] = useState(null);
  const [fileErr, setFileErr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const canSubmit = title.trim() && file && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      if (description.trim()) fd.append('description', description.trim());
      if (reviewerInstructions.trim()) fd.append('reviewer_instructions', reviewerInstructions.trim().slice(0, 500));
      fd.append('status', status);
      fd.append('visible_to_company_users', visible ? 'true' : 'false');
      fd.append('access_contact_ids', JSON.stringify(selected));
      fd.append('file', file);
      await axios.post(`${API}/admin/vdr/${companyId}/documents`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      onUploaded();
    } catch (e1) {
      setErr(e1.response?.data?.detail || 'Upload failed');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#161B22] border border-gray-700 rounded-xl p-6 max-w-xl w-full max-h-[92vh] overflow-y-auto" data-testid="upload-doc-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Upload className="w-5 h-5 text-[#00E87A]" /> Upload Document
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-1.5">Title <span className="text-red-400">*</span></label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} type="text"
                   className="w-full bg-[#0D1117] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-[#00E87A] focus:outline-none"
                   data-testid="upload-title" />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1.5">Description <span className="text-gray-500 text-xs">(optional)</span></label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
                      className="w-full bg-[#0D1117] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-[#00E87A] focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1.5">
              Instructions for Reviewers <span className="text-gray-500 text-xs">(optional · max 500 chars)</span>
            </label>
            <textarea value={reviewerInstructions} onChange={(e) => setReviewerInstructions(e.target.value.slice(0, 500))} rows={2}
                      placeholder="e.g. Please review Clause 4.2 on page 3. Focus on the indemnity section."
                      className="w-full bg-[#0D1117] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-[#00E87A] focus:outline-none"
                      data-testid="upload-reviewer-instructions" />
            <p className="text-xs text-gray-500 mt-1 text-right">{reviewerInstructions.length} / 500</p>
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1.5">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
                    className="w-full bg-[#0D1117] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-[#00E87A] focus:outline-none">
              {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <label className="flex items-center justify-between bg-[#0D1117] border border-gray-700 rounded-lg px-3 py-3 cursor-pointer">
            <div>
              <p className="text-sm text-white font-medium">Visible to all company users</p>
              <p className="text-xs text-gray-500 mt-0.5">Owners and secondary users on the platform can see this document.</p>
            </div>
            <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} className="w-4 h-4 accent-[#00E87A]" />
          </label>

          <PartyPicker
            initialParties={parties}
            selected={selected}
            onChange={setSelected}
            companyId={companyId}
            token={token}
            showToast={showToast}
            onPartyCreated={onPartyCreated}
          />

          <FilePicker file={file} onChange={setFile} error={fileErr} setError={setFileErr} />

          {err && <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-sm text-red-400">{err}</div>}

          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-300 hover:text-white text-sm">Cancel</button>
            <button type="submit" disabled={!canSubmit}
                    className="px-4 py-2 bg-[#00E87A] hover:bg-[#00d96e] text-black rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                    data-testid="upload-submit">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Upload & Share
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Upload New Version modal ─────────────────────────────────────────────
const VersionModal = ({ doc, companyId, token, onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [fileErr, setFileErr] = useState('');
  const [note, setNote] = useState('');
  const [instructions, setInstructions] = useState(doc.reviewer_instructions || '');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault(); setErr('');
    if (!file) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (note.trim()) fd.append('change_note', note.trim());
      // Always send — empty string clears the field for this version.
      fd.append('reviewer_instructions', instructions.trim().slice(0, 500));
      await axios.post(`${API}/admin/vdr/${companyId}/documents/${doc.id}/version`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      onUploaded();
    } catch (e1) {
      setErr(e1.response?.data?.detail || 'Upload failed'); setSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#161B22] border border-gray-700 rounded-xl p-6 max-w-lg w-full" data-testid="version-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-[#00E87A]" /> Upload New Version
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-gray-400 mb-3">{doc.title}</p>
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-xs text-amber-300 mb-4 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Uploading a new version will reset all approvals. All parties will be notified by email.</span>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <FilePicker file={file} onChange={setFile} error={fileErr} setError={setFileErr} />
          <div>
            <label className="block text-sm text-gray-300 mb-1.5">Change note <span className="text-gray-500 text-xs">(optional)</span></label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                      placeholder="Briefly describe what changed"
                      className="w-full bg-[#0D1117] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-[#00E87A] focus:outline-none"
                      data-testid="version-change-note" />
          </div>
          <div>
            <label className="flex items-center justify-between text-sm text-gray-300 mb-1.5">
              <span>Reviewer Instructions <span className="text-gray-500 text-xs">(optional, max 500 chars)</span></span>
              <span className="text-[10px] text-gray-500">{instructions.length}/500</span>
            </label>
            <textarea value={instructions}
                      onChange={(e) => setInstructions(e.target.value.slice(0, 500))} rows={3}
                      placeholder="e.g. Please re-check Clause 4.2 — language updated since v1."
                      className="w-full bg-[#0D1117] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-[#00E87A] focus:outline-none"
                      data-testid="version-reviewer-instructions" />
            <p className="text-[11px] text-gray-500 mt-1">
              Pre-filled from the current version. Edit before upload — each version keeps its own copy.
            </p>
          </div>
          {err && <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-sm text-red-400">{err}</div>}
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-300 hover:text-white text-sm">Cancel</button>
            <button type="submit" disabled={!file || submitting}
                    className="px-4 py-2 bg-[#00E87A] hover:bg-[#00d96e] text-black rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                    data-testid="version-modal-submit">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Upload
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Edit Document modal ──────────────────────────────────────────────────
const EditDocModal = ({ doc, companyId, token, parties, onClose, onSaved, showToast, onPartyCreated }) => {
  const [title, setTitle] = useState(doc.title || '');
  const [description, setDescription] = useState(doc.description || '');
  const [reviewerInstructions, setReviewerInstructions] = useState(doc.reviewer_instructions || '');
  const [status, setStatus] = useState(doc.status || 'draft');
  const [visible, setVisible] = useState(!!doc.visible_to_company_users);
  const [selected, setSelected] = useState(doc.access_contact_ids || []);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault(); setErr(''); setSubmitting(true);
    try {
      await axios.patch(`${API}/admin/vdr/${companyId}/documents/${doc.id}`, {
        title: title.trim(), description: description.trim() || null,
        reviewer_instructions: reviewerInstructions.trim().slice(0, 500) || null,
        status,
        visible_to_company_users: visible, access_contact_ids: selected,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved();
    } catch (e1) {
      setErr(e1.response?.data?.detail || 'Save failed'); setSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#161B22] border border-gray-700 rounded-xl p-6 max-w-xl w-full max-h-[92vh] overflow-y-auto" data-testid="edit-doc-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Pencil className="w-5 h-5 text-[#00E87A]" /> Edit Document
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div><label className="block text-sm text-gray-300 mb-1.5">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full bg-[#0D1117] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-[#00E87A] focus:outline-none" /></div>
          <div><label className="block text-sm text-gray-300 mb-1.5">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full bg-[#0D1117] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-[#00E87A] focus:outline-none" /></div>
          <div>
            <label className="block text-sm text-gray-300 mb-1.5">
              Instructions for Reviewers <span className="text-gray-500 text-xs">(optional · max 500 chars)</span>
            </label>
            <textarea value={reviewerInstructions} onChange={(e) => setReviewerInstructions(e.target.value.slice(0, 500))} rows={2}
                      placeholder="e.g. Please review Clause 4.2 on page 3."
                      className="w-full bg-[#0D1117] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-[#00E87A] focus:outline-none"
                      data-testid="edit-reviewer-instructions" />
            <p className="text-xs text-gray-500 mt-1 text-right">{reviewerInstructions.length} / 500</p>
          </div>
          <div><label className="block text-sm text-gray-300 mb-1.5">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full bg-[#0D1117] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-[#00E87A] focus:outline-none">
              {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select></div>
          <label className="flex items-center justify-between bg-[#0D1117] border border-gray-700 rounded-lg px-3 py-3 cursor-pointer">
            <span className="text-sm text-white">Visible to all company users</span>
            <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} className="w-4 h-4 accent-[#00E87A]" />
          </label>

          <PartyPicker
            initialParties={parties}
            selected={selected}
            onChange={setSelected}
            companyId={companyId}
            token={token}
            showToast={showToast}
            onPartyCreated={onPartyCreated}
          />

          {err && <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-sm text-red-400">{err}</div>}
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-300 hover:text-white text-sm">Cancel</button>
            <button type="submit" disabled={submitting} className="px-4 py-2 bg-[#00E87A] hover:bg-[#00d96e] text-black rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Delete Confirm ───────────────────────────────────────────────────────
const DeleteDocModal = ({ doc, companyId, token, onClose, onDeleted }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const go = async () => {
    setBusy(true); setErr('');
    try {
      await axios.delete(`${API}/admin/vdr/${companyId}/documents/${doc.id}`, { headers: { Authorization: `Bearer ${token}` } });
      onDeleted();
    } catch (e) { setErr(e.response?.data?.detail || 'Delete failed'); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#161B22] border border-gray-700 rounded-xl p-6 max-w-md w-full">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center"><Trash2 className="w-5 h-5 text-red-400" /></div>
          <h3 className="text-lg font-semibold text-white">Remove Document?</h3>
        </div>
        <p className="text-gray-300 text-sm mb-4">Remove <strong className="text-white">{doc.title}</strong> from the Data Room? All activity history is preserved and the file remains in storage.</p>
        {err && <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 mb-3 text-sm text-red-400">{err}</div>}
        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-gray-300 hover:text-white text-sm">Cancel</button>
          <button onClick={go} disabled={busy} className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Remove
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Document Detail Panel (slide-in from right) ─────────────────────────
const ApprovalRow = ({ ap, activity, currentVer }) => {
  let icon, color, label;
  if (ap.status === 'approved') {
    icon = <CheckCircle2 className="w-4 h-4" />; color = 'text-[#00E87A]'; label = 'Approved';
  } else if (ap.status === 'changes_requested') {
    icon = <RefreshCw className="w-4 h-4" />; color = 'text-orange-400'; label = 'Changes Requested';
  } else {
    icon = <Clock className="w-4 h-4" />; color = 'text-gray-400'; label = 'Pending';
  }
  // detect open status from activity
  const opened = activity.some(
    (e) => e.action === 'document_viewed'
      && e.version_number === currentVer
      && (e.performed_by_email || '').toLowerCase() === (ap.email || '').toLowerCase(),
  );
  return (
    <div className="py-2.5 border-b border-gray-700/60 last:border-0">
      <div className="grid grid-cols-12 gap-2 items-center text-sm">
        <div className="col-span-4">
          <p className="text-white">{ap.name || ap.email}</p>
          <p className="text-xs text-gray-500">{ap.email}</p>
        </div>
        <div className="col-span-3 text-xs text-gray-300">{ap.user_type === 'company_user' ? 'Company User' : (ap.role_label || ap.user_type || '—')}</div>
        <div className={`col-span-3 inline-flex items-center gap-1.5 ${color}`}>{icon}<span className="text-xs font-medium">{label}</span></div>
        <div className="col-span-2 text-xs text-gray-400 text-right">{ap.responded_at ? fmtDateTime(ap.responded_at) : '—'}</div>
      </div>
      {ap.status === 'changes_requested' && ap.comment_text && (
        <div className="mt-2 ml-1 text-xs text-orange-300 bg-orange-500/10 rounded p-2 italic">&ldquo;{ap.comment_text}&rdquo;</div>
      )}
      {ap.status === 'pending' && (
        <p className="mt-1 ml-1 text-xs text-gray-500">{opened ? 'opened, not responded' : 'not yet opened'}</p>
      )}
    </div>
  );
};

const ActivityThread = ({ doc, companyId, token, activity, onRefresh }) => {
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);
  const submit = async () => {
    if (!comment.trim() || posting) return;
    setPosting(true);
    try {
      await axios.post(`${API}/admin/vdr/documents/${doc.id}/comments`,
        { comment_text: comment.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      setComment('');
      onRefresh();
    } finally { setPosting(false); }
  };
  return (
    <div>
      <h4 className="text-sm font-medium text-white mb-3">Activity</h4>
      <div className="space-y-2.5 max-h-72 overflow-y-auto pr-1">
        {activity.length === 0 && <p className="text-xs text-gray-500">No activity yet.</p>}
        {activity.map((e) => {
          const isComment = e.entry_type === 'comment';
          const isVer = e.action === 'new_version_uploaded';
          const isReset = e.action === 'approvals_reset';
          return (
            <div key={e.id} className="text-xs">
              <span className="inline-block px-1.5 py-0.5 rounded bg-gray-700/60 text-gray-300 mr-2 text-[10px] font-mono">v{e.version_number ?? '-'}</span>
              {isReset ? (
                <span className="text-gray-500 italic">── All approvals reset ──</span>
              ) : isVer ? (
                <span className="text-gray-400">Version {e.version_number} uploaded · {fmtDateTime(e.created_at)}</span>
              ) : isComment ? (
                <div className="inline">
                  <span className="text-gray-400">{e.performed_by_name || e.performed_by_email}</span>
                  <span className="text-gray-500"> · {fmtDateTime(e.created_at)}</span>
                  <p className="mt-1 ml-7 text-white">{e.comment_text}</p>
                </div>
              ) : (
                <span className="text-gray-500">
                  {prettyAction(e.action)} · {e.performed_by_name || e.performed_by_email} · {fmtDateTime(e.created_at)}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex gap-2">
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
                  placeholder="Add a comment..."
                  className="flex-1 bg-[#0D1117] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#00E87A] focus:outline-none" />
        <button onClick={submit} disabled={!comment.trim() || posting}
                className="self-end px-3 py-2 bg-[#00E87A] hover:bg-[#00d96e] text-black rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center gap-1.5"
                data-testid="post-comment-btn">
          {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Post
        </button>
      </div>
    </div>
  );
};

const DocumentDetailPanel = ({ docId, companyId, token, onClose, onChanged, partiesById }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showVerDropdown, setShowVerDropdown] = useState(false);
  const [showVerModal, setShowVerModal] = useState(false);

  const fetchDoc = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/vdr/${companyId}/documents/${docId}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setDoc(r.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchDoc(); }, [docId]); // eslint-disable-line

  const currentVer = useMemo(() => doc?.versions?.find((v) => v.is_current), [doc]);

  // Always open with a freshly minted presigned URL so links that have been
  // sitting on screen past their 15-min TTL still work.
  const openFreshUrl = async (versionNumber) => {
    try {
      const r = await axios.get(
        `${API}/admin/vdr/${companyId}/documents/${docId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDoc(r.data);
      const v = (r.data?.versions || []).find(
        (x) => x.version_number === versionNumber,
      ) || (r.data?.versions || []).find((x) => x.is_current);
      if (v?.presigned_url) window.open(v.presigned_url, '_blank', 'noopener');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to refresh document link. Please retry.');
    }
  };

  // Enrich approvals with role_label for contact rows
  const approvalsEnriched = (currentVer?.approvals || []).map((a) => ({
    ...a, role_label: a.contact_id ? (partiesById[a.contact_id]?.role_label) : null,
  }));

  return (
    <div className="fixed inset-y-0 right-0 w-[600px] bg-[#0D1117] border-l border-gray-700 shadow-2xl z-40 overflow-y-auto" data-testid="doc-detail-panel">
      <div className="sticky top-0 bg-[#0D1117] border-b border-gray-700 px-5 py-3 flex items-center justify-between z-10">
        <h3 className="text-white font-semibold flex items-center gap-2"><FileText className="w-5 h-5 text-[#00E87A]" /> Document</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="doc-detail-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-5">
        {loading ? <div className="flex items-center gap-2 text-gray-400 py-8"><Loader2 className="w-5 h-5 animate-spin" /> Loading...</div>
         : err ? <div className="text-red-400 text-sm">{err}</div>
         : doc && (
          <>
            <div className="mb-5">
              <h2 className="text-xl font-semibold text-white">{doc.title}</h2>
              {doc.description && <p className="text-sm text-gray-400 mt-1">{doc.description}</p>}
              {doc.current_change_note && (
                <div className="mt-3 bg-amber-500/10 border-l-4 border-amber-400 rounded p-3 text-sm text-amber-100" data-testid="admin-whats-new-banner">
                  <p className="text-xs font-semibold text-amber-300 uppercase tracking-wide mb-1">What&apos;s new in v{doc.current_version_number}</p>
                  <p className="whitespace-pre-wrap">{doc.current_change_note}</p>
                </div>
              )}
              {doc.reviewer_instructions && (
                <div className="mt-3 bg-amber-500/10 border-l-4 border-amber-400 rounded p-3 text-sm text-amber-200" data-testid="admin-reviewer-instructions">
                  <p className="text-xs font-semibold text-amber-300 uppercase tracking-wide mb-1">Reviewer Instructions</p>
                  <p className="whitespace-pre-wrap">{doc.reviewer_instructions}</p>
                </div>
              )}
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <StatusPill status={doc.status} />
                <span className="text-xs text-gray-300">Version {doc.current_version_number}</span>
              </div>
              <div className="mt-3 flex gap-2 flex-wrap">
                {currentVer && (
                  <button type="button"
                          onClick={() => openFreshUrl(currentVer.version_number)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#00E87A]/15 hover:bg-[#00E87A]/25 text-[#00E87A] rounded-lg text-xs font-medium"
                          data-testid="admin-doc-download-current">
                    <Download className="w-3.5 h-3.5" /> Download v{doc.current_version_number}
                  </button>
                )}
                <button onClick={() => setShowVerModal(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-gray-200 rounded-lg text-xs font-medium">
                  <RefreshCw className="w-3.5 h-3.5" /> Upload New Version
                </button>
                {doc.versions?.length > 1 && (
                  <div className="relative">
                    <button onClick={() => setShowVerDropdown(!showVerDropdown)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-gray-200 rounded-lg text-xs font-medium">
                      Version History <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                    {showVerDropdown && (
                      <div className="absolute top-full mt-1 right-0 bg-[#161B22] border border-gray-700 rounded-lg shadow-xl p-1 z-10 w-72">
                        {[...doc.versions].sort((a, b) => b.version_number - a.version_number).map((v) => (
                          <button key={v.version_number} type="button"
                             onClick={() => { setShowVerDropdown(false); openFreshUrl(v.version_number); }}
                             className="block w-full text-left px-3 py-2 text-xs hover:bg-white/5 rounded">
                            <div className="flex items-center justify-between">
                              <span className="text-white font-medium">v{v.version_number}</span>
                              <span className="text-gray-500">{fmtDate(v.uploaded_at)}</span>
                            </div>
                            {v.change_note && <p className="text-gray-400 mt-0.5">{v.change_note}</p>}
                            {v.reviewer_instructions && (
                              <p className="text-amber-300/80 mt-1 text-[11px] italic line-clamp-2">
                                ℹ️ {v.reviewer_instructions}
                              </p>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Admin-only: mark for physical signing (whole-document, one-shot) */}
            <AdminMarkPhysicalPanel
              doc={doc}
              docEndpointBase={`${API}/admin/vdr/${companyId}/documents/${docId}`}
              authHeaders={{ Authorization: `Bearer ${token}` }}
              onRefresh={fetchDoc}
            />

            {/* Execute Agreement panel (also surfaces the executed PDF + certificate downloads) */}
            <ExecutionPanel
              doc={doc}
              docEndpointBase={`${API}/admin/vdr/${companyId}/documents/${docId}`}
              authHeaders={{ Authorization: `Bearer ${token}` }}
              onRefresh={fetchDoc}
              testIdPrefix="admin"
              canExecute={true}
            />

            <div className="mb-5">
              <h4 className="text-sm font-medium text-white mb-2">Approval Status — Version {doc.current_version_number}</h4>
              <div className="bg-[#161B22] border border-gray-700 rounded-lg p-3">
                <div className="grid grid-cols-12 gap-2 pb-2 border-b border-gray-700/60 text-[11px] uppercase tracking-wide text-gray-500">
                  <div className="col-span-4">Name</div>
                  <div className="col-span-3">Role</div>
                  <div className="col-span-3">Status</div>
                  <div className="col-span-2 text-right">Responded</div>
                </div>
                {approvalsEnriched.length === 0
                  ? <p className="text-xs text-gray-500 py-3 text-center">No reviewers on this version</p>
                  : approvalsEnriched.map((ap, i) => (
                      <ApprovalRow key={i} ap={ap} activity={doc.activity || []} currentVer={doc.current_version_number} />
                    ))}
              </div>
            </div>

            <ActivityThread doc={doc} companyId={companyId} token={token}
                             activity={doc.activity || []} onRefresh={fetchDoc} />

            {showVerModal && (
              <VersionModal doc={doc} companyId={companyId} token={token}
                            onClose={() => setShowVerModal(false)}
                            onUploaded={() => { setShowVerModal(false); fetchDoc(); onChanged(); }} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── Activity Log sub-section ─────────────────────────────────────────────
const ActivityLogSection = ({ companyId, token, parties, documents }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filters, setFilters] = useState({ document_id: '', email: '', action: '', date_from: '', date_to: '' });
  const [exporting, setExporting] = useState(null);

  const fetchRows = async () => {
    setLoading(true); setErr('');
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await axios.get(`${API}/admin/vdr/${companyId}/activity`,
        { params, headers: { Authorization: `Bearer ${token}` } });
      setRows(r.data || []);
    } catch (e) { setErr(e.response?.data?.detail || 'Failed to load activity'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchRows(); }, [companyId, filters]); // eslint-disable-line

  const allActions = useMemo(() => {
    const s = new Set(); rows.forEach((r) => { if (r.action) s.add(r.action); });
    return [...s].sort();
  }, [rows]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const r = await axios.get(`${API}/admin/vdr/${companyId}/audit-export`,
        { params: { format }, responseType: 'blob', headers: { Authorization: `Bearer ${token}` } });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `vdr-audit.${format}`; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { /* swallow */ }
    finally { setExporting(null); }
  };

  const clearFilters = () => setFilters({ document_id: '', email: '', action: '', date_from: '', date_to: '' });
  const partiesByEmail = useMemo(() => {
    const m = {}; parties.forEach((p) => { m[p.email] = p; }); return m;
  }, [parties]);
  const partyOptions = parties; // include disabled ones — log can have older entries

  return (
    <div className="space-y-4" data-testid="activity-log-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-lg font-semibold text-white">Activity Log</h3>
        <div className="flex gap-2">
          <button onClick={() => handleExport('csv')} disabled={exporting === 'csv'}
                  className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-gray-200 rounded-lg text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="export-csv-btn">
            {exporting === 'csv' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} CSV
          </button>
          <button onClick={() => handleExport('pdf')} disabled={exporting === 'pdf'}
                  className="px-3 py-1.5 bg-[#00E87A]/15 hover:bg-[#00E87A]/25 text-[#00E87A] rounded-lg text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="export-pdf-btn">
            {exporting === 'pdf' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#161B22] border border-gray-700 rounded-xl p-3 flex flex-wrap gap-2 items-center text-sm">
        <select value={filters.document_id} onChange={(e) => setFilters({ ...filters, document_id: e.target.value })}
                className="bg-[#0D1117] border border-gray-700 rounded px-2 py-1.5 text-white" data-testid="filter-doc">
          <option value="">All Documents</option>
          {documents.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
        </select>
        <select value={filters.email} onChange={(e) => setFilters({ ...filters, email: e.target.value })}
                className="bg-[#0D1117] border border-gray-700 rounded px-2 py-1.5 text-white" data-testid="filter-party">
          <option value="">All People</option>
          {partyOptions.map((p) => <option key={p.id} value={p.email}>{p.name}</option>)}
        </select>
        <select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                className="bg-[#0D1117] border border-gray-700 rounded px-2 py-1.5 text-white">
          <option value="">All Actions</option>
          {allActions.map((a) => <option key={a} value={a}>{prettyAction(a)}</option>)}
        </select>
        <input type="date" value={filters.date_from?.slice(0, 10) || ''}
               onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
               className="bg-[#0D1117] border border-gray-700 rounded px-2 py-1.5 text-white" />
        <input type="date" value={filters.date_to?.slice(0, 10) || ''}
               onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
               className="bg-[#0D1117] border border-gray-700 rounded px-2 py-1.5 text-white" />
        {(filters.document_id || filters.email || filters.action || filters.date_from || filters.date_to) && (
          <button onClick={clearFilters} className="text-xs text-gray-400 hover:text-white flex items-center gap-1">
            <FilterX className="w-3.5 h-3.5" /> Clear
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? <div className="flex items-center gap-2 text-gray-400 py-8 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading...</div>
       : err ? <div className="text-red-400 text-sm">{err}</div>
       : rows.length === 0 ? <div className="bg-[#161B22] border border-gray-700 rounded-xl py-12 text-center text-gray-400 text-sm">No activity matches the current filters.</div>
       : (
        <div className="bg-[#161B22] border border-gray-700 rounded-xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0D1117] border-b border-gray-700">
              <tr>
                <th className="text-left px-4 py-2.5 text-gray-400 font-medium">Version</th>
                <th className="text-left px-4 py-2.5 text-gray-400 font-medium">Date</th>
                <th className="text-left px-4 py-2.5 text-gray-400 font-medium">Time</th>
                <th className="text-left px-4 py-2.5 text-gray-400 font-medium">Who</th>
                <th className="text-left px-4 py-2.5 text-gray-400 font-medium">Action</th>
                <th className="text-left px-4 py-2.5 text-gray-400 font-medium">Document</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/60">
              {rows.map((r) => {
                const docTitle = documents.find((d) => d.id === r.document_id)?.title || '—';
                const dt = r.created_at ? new Date(r.created_at) : null;
                return (
                  <tr key={r.id} className="hover:bg-white/5">
                    <td className="px-4 py-2 text-gray-300 text-xs font-mono">v{r.version_number ?? '-'}</td>
                    <td className="px-4 py-2 text-gray-300">{dt ? dt.toLocaleDateString('en-US') : '—'}</td>
                    <td className="px-4 py-2 text-gray-400 text-xs">{dt ? dt.toLocaleTimeString('en-US') : '—'}</td>
                    <td className="px-4 py-2 text-gray-300">
                      <div className="text-white">{r.performed_by_name || r.performed_by_email || '—'}</div>
                      <div className="text-xs text-gray-500">{r.performed_by_type}</div>
                    </td>
                    <td className="px-4 py-2 text-gray-300">{r.entry_type === 'comment' ? 'Comment' : prettyAction(r.action)}</td>
                    <td className="px-4 py-2 text-gray-400 text-xs">{docTitle}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Main Document Room Tab ───────────────────────────────────────────────
const AdminDocumentRoomTab = ({ companyId, companyName }) => {
  const [docs, setDocs] = useState([]);
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [subtab, setSubtab] = useState('documents');
  const [showUpload, setShowUpload] = useState(false);
  const [detailDocId, setDetailDocId] = useState(null);
  const [versionDoc, setVersionDoc] = useState(null);
  const [editDoc, setEditDoc] = useState(null);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [toast, setToast] = useState(null);

  const token = localStorage.getItem('auth_token');
  const showToast = (text, kind = 'success') => {
    setToast({ text, kind }); setTimeout(() => setToast(null), 3000);
  };

  const fetchAll = async () => {
    setLoading(true); setErr('');
    try {
      const [d, p] = await Promise.all([
        axios.get(`${API}/admin/vdr/${companyId}/documents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/companies/${companyId}/contacts`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setDocs(d.data || []); setParties(p.data || []);
    } catch (e) { setErr(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, [companyId]); // eslint-disable-line

  const partiesById = useMemo(() => {
    const m = {}; parties.forEach((p) => { m[p.id] = p; }); return m;
  }, [parties]);

  return (
    <div className="space-y-5" data-testid="admin-docroom-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#00E87A]" /> Data Room
          </h2>
          <p className="text-sm text-gray-400 mt-0.5">Share, version, and audit legal documents for {companyName || 'this company'}</p>
        </div>
        {subtab === 'documents' && (
          <button onClick={() => setShowUpload(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#00E87A] hover:bg-[#00d96e] text-black rounded-lg text-sm font-semibold"
                  data-testid="docroom-upload-btn">
            <Plus className="w-4 h-4" /> Upload Document
          </button>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-gray-700/60">
        {[{ id: 'documents', label: 'Documents' }, { id: 'activity', label: 'Activity Log' }].map((t) => (
          <button key={t.id} onClick={() => setSubtab(t.id)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    subtab === t.id ? 'border-[#00E87A] text-[#00E87A]' : 'border-transparent text-gray-400 hover:text-white'
                  }`}
                  data-testid={`docroom-subtab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {subtab === 'documents' && (
        loading ? <div className="flex items-center gap-2 text-gray-400 py-8 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading...</div>
        : err ? <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {err}<button onClick={fetchAll} className="ml-auto text-xs underline">Retry</button></div>
        : docs.length === 0 ? (
          <div className="bg-[#161B22] border border-gray-700 rounded-xl py-16 text-center" data-testid="docroom-empty">
            <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-300 font-medium">No documents in this Data Room yet.</p>
            <p className="text-gray-500 text-sm mt-1">Upload your first PDF to start collecting approvals.</p>
          </div>
        ) : (
          <div className="bg-[#161B22] border border-gray-700 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0D1117] border-b border-gray-700">
                  <tr>
                    <th className="text-left px-4 py-3 text-gray-400 font-medium">Title</th>
                    <th className="text-left px-4 py-3 text-gray-400 font-medium">Version</th>
                    <th className="text-left px-4 py-3 text-gray-400 font-medium">Status</th>
                    <th className="text-left px-4 py-3 text-gray-400 font-medium">Uploaded</th>
                    <th className="text-left px-4 py-3 text-gray-400 font-medium">Approvals</th>
                    <th className="text-right px-4 py-3 text-gray-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/60">
                  {docs.map((d) => (
                    <tr key={d.id} className="hover:bg-white/5" data-testid={`doc-row-${d.id}`}>
                      <td className="px-4 py-3">
                        <button onClick={() => setDetailDocId(d.id)} className="text-white hover:text-[#00E87A] text-left">
                          {d.title}
                        </button>
                        {d.description && <p className="text-xs text-gray-500 mt-0.5 max-w-xs truncate">{d.description}</p>}
                      </td>
                      <td className="px-4 py-3 text-gray-300 text-xs font-mono">v{d.current_version_number}</td>
                      <td className="px-4 py-3"><StatusPill status={d.status} /></td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{fmtDate(d.created_at)}</td>
                      <td className="px-4 py-3"><ApprovalSummaryCell summary={d.approval_summary} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 justify-end">
                          <button onClick={() => setDetailDocId(d.id)} title="View" className="p-1.5 rounded hover:bg-white/10 text-gray-300" data-testid={`doc-view-${d.id}`}><Eye className="w-4 h-4" /></button>
                          <button onClick={() => setVersionDoc(d)} title="Upload New Version" className="p-1.5 rounded hover:bg-[#00E87A]/15 text-[#00E87A]"><RefreshCw className="w-4 h-4" /></button>
                          <button onClick={() => setEditDoc(d)} title="Edit" className="p-1.5 rounded hover:bg-white/10 text-gray-300"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => setDeleteDoc(d)} title="Remove" className="p-1.5 rounded hover:bg-red-500/15 text-red-400"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {subtab === 'activity' && (
        <ActivityLogSection companyId={companyId} token={token} parties={parties} documents={docs} />
      )}

      {/* Modals */}
      {showUpload && <UploadDocModal companyId={companyId} token={token} parties={parties} showToast={showToast}
                                     onPartyCreated={fetchAll}
                                     onClose={() => setShowUpload(false)}
                                     onUploaded={() => { setShowUpload(false); fetchAll(); showToast('Document uploaded'); }} />}
      {versionDoc && <VersionModal doc={versionDoc} companyId={companyId} token={token}
                                    onClose={() => setVersionDoc(null)}
                                    onUploaded={() => { setVersionDoc(null); fetchAll(); showToast('New version uploaded'); }} />}
      {editDoc && <EditDocModal doc={editDoc} companyId={companyId} token={token} parties={parties} showToast={showToast}
                                 onPartyCreated={fetchAll}
                                 onClose={() => setEditDoc(null)}
                                 onSaved={() => { setEditDoc(null); fetchAll(); showToast('Document saved'); }} />}
      {deleteDoc && <DeleteDocModal doc={deleteDoc} companyId={companyId} token={token}
                                     onClose={() => setDeleteDoc(null)}
                                     onDeleted={() => { setDeleteDoc(null); fetchAll(); showToast('Document removed'); }} />}
      {detailDocId && <DocumentDetailPanel docId={detailDocId} companyId={companyId} token={token}
                                            partiesById={partiesById}
                                            onClose={() => setDetailDocId(null)}
                                            onChanged={() => fetchAll()} />}

      {toast && (
        <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-lg text-sm font-medium z-50 ${toast.kind === 'error' ? 'bg-red-500/90 text-white' : 'bg-[#00E87A]/95 text-black'}`}>
          {toast.text}
        </div>
      )}
    </div>
  );
};

export default AdminDocumentRoomTab;
